import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  doc, onSnapshot, updateDoc, collection, query, where,
  orderBy, getDocs, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { downloadInvoicePdf } from '../../utils/downloadInvoicePdf';
import MessageThread from '../../components/MessageThread';
import ParticipantList from '../../components/ParticipantList';
import ConfirmDialog from '../../components/ConfirmDialog';
import TeamsTab, { teamsLayoutRef } from '../../components/TeamsTab';
import { ActivityMatches } from '../../components/TeamFixtures';
import useTourTeams from '../../hooks/useTourTeams';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePreviewNavigate } from '../../contexts/PreviewContext';
import { STATUS_STYLES, STATUS_LABELS, QUOTE_SENT_STATUSES, PARTICIPANT_STATUSES } from '../../utils/statuses';
import { totalTravellers } from '../../utils/groupSize';
import { dayEntries, slotLabel } from '../../utils/skeletalLayout';

// ── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'overview',     label: 'Overview'    },
  { key: 'participants', label: 'Participants' },
  { key: 'invoices',     label: 'Invoices'    },
  { key: 'itineraries', label: 'Itineraries'  },
  { key: 'rooming',      label: 'Rooming'     },
  { key: 'teams',        label: 'Teams'       },
  { key: 'documents',    label: 'Documents'   },
  { key: 'calendar',     label: 'Calendar'    },
  { key: 'messages',     label: 'Messages'    },
];

const TAB_ICONS = {
  overview:     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>,
  participants: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  invoices:     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" /></svg>,
  itineraries:  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>,
  teams:        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg>,
  rooming:      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>,
  documents:    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>,
  calendar:     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5" /></svg>,
  messages:     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" /></svg>,
};

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}
function formatAmount(n, sym = '£') {
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function numDays(dep, ret) {
  if (!dep || !ret) return 0;
  return Math.max(1, Math.round((new Date(ret) - new Date(dep)) / 86400000) + 1);
}
function dayLabel(dep, idx) {
  if (!dep) return `Day ${idx + 1}`;
  const d = new Date(dep); d.setDate(d.getDate() + idx);
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-0.5">{label}</p>
      <p className="text-[13px] text-[#111827]">{value}</p>
    </div>
  );
}

function FileIcon({ mime }) {
  const ext = mime?.includes('pdf') ? 'PDF' : mime?.includes('image') ? 'IMG' : mime?.includes('word') || mime?.includes('document') ? 'DOC' : mime?.includes('sheet') || mime?.includes('excel') ? 'XLS' : 'FILE';
  const colors = { PDF: 'bg-red-100 text-red-700', IMG: 'bg-green-100 text-green-700', DOC: 'bg-blue-100 text-blue-700', XLS: 'bg-emerald-100 text-emerald-700', FILE: 'bg-gray-100 text-gray-600' };
  return <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${colors[ext] || colors.FILE}`}>{ext}</span>;
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function CustomerTourDetail({ id: idProp } = {}) {
  const params = useParams();
  const id = idProp || params.id;
  const navigate = usePreviewNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const validTabKeys = TABS.map((t) => t.key);
  const hashTab = location.hash.replace('#', '');
  const [activeTab, setActiveTab] = useState(validTabKeys.includes(hashTab) ? hashTab : 'overview');
  const [request, setRequest]           = useState(null);
  const [loading, setLoading]           = useState(true);

  // Per-tab data
  const [invoices, setInvoices]           = useState([]);
  const [documents, setDocuments]         = useState([]);
  const [sentLetters, setSentLetters]     = useState([]);
  const [sentResources, setSentResources] = useState([]);
  const [customerDocs, setCustomerDocs]   = useState([]);
  const [skeletalLayout, setSkeletalLayout] = useState({ grid: {} });
  const [itineraryItems, setItineraryItems]         = useState([]);
  const [activeItinDay, setActiveItinDay]           = useState(0);
  const [skeletalMetaApproved, setSkeletalMetaApproved] = useState(false);
  const [roomingLayout, setRoomingLayout] = useState(null);
  // The Teams tab only appears once MST has created at least one team.
  const [hasTeams, setHasTeams] = useState(false);
  // Teams (made from the Team Info squads) + fixtures, for the matches under
  // a Fixtures activity. No opposition/referee contacts here — staff only.
  const tourTeams = useTourTeams(id, request, { sync: !!request });
  const fixturesById = useMemo(
    () => Object.fromEntries(tourTeams.fixtures.map((f) => [f.id, f])),
    [tourTeams.fixtures]
  );
  const [participants, setParticipants]   = useState([]);

  // Quote actions
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [showAmendForm, setShowAmendForm]     = useState(false);
  const [amendText, setAmendText]             = useState('');
  const [confirmDecline, setConfirmDecline]   = useState(false);
  const [downloadingId, setDownloadingId]     = useState(null);

  // Documents upload
  const [uploading, setUploading]       = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Rooming drag state
  const [draggedParticipant, setDraggedParticipant] = useState(null);
  const [roomAssignments, setRoomAssignments] = useState({});
  const [savingRooming, setSavingRooming] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'requests', id), (snap) => {
      if (snap.exists()) setRequest({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
    return unsub;
  }, [id]);

  useEffect(() => {
    const q = query(collection(db, 'invoices'), where('requestId', '==', id), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'documents') return;
    const q = query(collection(db, 'documents'), where('requestId', '==', id), where('visibleToCustomer', '==', true));
    const unsub = onSnapshot(q, (snap) => setDocuments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const unsub2 = onSnapshot(collection(db, 'requests', id, 'sentLetters'), (snap) => setSentLetters(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const q3 = query(collection(db, 'customerDocuments'), where('requestId', '==', id));
    const unsub3 = onSnapshot(q3, (snap) => setCustomerDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const unsub4 = onSnapshot(collection(db, 'requests', id, 'sentResources'), (snap) => setSentResources(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => { unsub(); unsub2(); unsub3(); unsub4(); };
  }, [id, activeTab]);

  useEffect(() => {
    if (activeTab !== 'itineraries') return;
    const unsub = onSnapshot(doc(db, 'requests', id, 'itineraryMeta', 'skeletal'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setSkeletalLayout({ grid: data.grid || {}, hiddenCells: data.hiddenCells || {}, dayOrder: data.dayOrder || {} });
        // Read approval state directly from the meta doc — more reliable than request.skeletalApprovalState
        setSkeletalMetaApproved(data.approvalState === 'approved');
      }
    });
    // Single-field orderBy avoids requiring a composite index
    getDocs(query(collection(db, 'requests', id, 'itinerary'), orderBy('day', 'asc'))).then((snap) => {
      setItineraryItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }).catch((err) => {
      console.error('Itinerary load failed:', err);
      // Fallback: load without ordering if index error
      getDocs(collection(db, 'requests', id, 'itinerary')).then((snap) => {
        setItineraryItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      });
    });
    return unsub;
  }, [id, activeTab]);

  useEffect(() => {
    const unsub = onSnapshot(teamsLayoutRef(id), (snap) => {
      setHasTeams(snap.exists() && (snap.data().teams || []).length > 0);
    }, () => setHasTeams(false));
    return unsub;
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'rooming' && activeTab !== 'overview') return;
    const unsub = onSnapshot(doc(db, 'requests', id, 'rooming', 'layout'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setRoomingLayout(data);
        setRoomAssignments(data.assignments || {});
      }
    });
    return unsub;
  }, [id, activeTab]);

  useEffect(() => {
    if (activeTab !== 'participants' && activeTab !== 'rooming') return;
    const unsub = onSnapshot(collection(db, 'requests', id, 'participants'), (snap) => {
      setParticipants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [id, activeTab]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  async function approveQuote() {
    setQuoteSubmitting(true);
    try {
      await updateDoc(doc(db, 'requests', id), { status: 'quote_approved', quoteApprovedAt: serverTimestamp() });
      showToast('Quote approved — we\'ll be in touch shortly.', 'success');
    } finally { setQuoteSubmitting(false); }
  }

  async function declineQuote() {
    setQuoteSubmitting(true);
    try {
      await updateDoc(doc(db, 'requests', id), { status: 'cancelled', quoteDeclinedAt: serverTimestamp() });
      showToast('Quote declined.', 'info');
      setConfirmDecline(false);
    } finally { setQuoteSubmitting(false); }
  }

  async function submitAmendment() {
    if (!amendText.trim()) return;
    setQuoteSubmitting(true);
    try {
      await updateDoc(doc(db, 'requests', id), {
        status: 'reviewing',
        customerAmendmentRequest: amendText.trim(),
        customerAmendmentRequestedAt: serverTimestamp(),
      });
      setShowAmendForm(false);
      setAmendText('');
      showToast('Amendment request sent.', 'success');
    } finally { setQuoteSubmitting(false); }
  }

  async function handleDocumentUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { showToast('File too large (max 20 MB).', 'error'); return; }
    setUploading(true);
    setUploadProgress(0);
    try {
      const path = `customerDocuments/${id}/${Date.now()}_${file.name}`;
      const sRef = storageRef(storage, path);
      const task = uploadBytesResumable(sRef, file);
      task.on('state_changed', (snap) => setUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)));
      await task;
      const url = await getDownloadURL(sRef);
      await addDoc(collection(db, 'customerDocuments'), {
        requestId: id, name: file.name, storagePath: path, downloadUrl: url,
        mimeType: file.type, size: file.size, uploadedByUid: currentUser.uid, createdAt: serverTimestamp(),
      });
      showToast('Document uploaded.', 'success');
    } catch (err) {
      console.error(err);
      showToast('Upload failed. Please try again.', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function submitRooming() {
    setSavingRooming(true);
    try {
      await updateDoc(doc(db, 'requests', id, 'rooming', 'layout'), {
        assignments: roomAssignments,
        status: 'submitted',
        submittedAt: serverTimestamp(),
      });
      showToast('Room assignments saved.', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to save. Please try again.', 'error');
    } finally { setSavingRooming(false); }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <p className="text-[#9CA3AF] text-[14px]">Loading…</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <p className="text-[#9CA3AF] text-[14px]">Tour not found.</p>
      </div>
    );
  }

  const sym = CURRENCY_SYMBOLS[request.currency] || '£';
  const tourTitle = request.tourName || `${request.sport} — ${request.destination}`;
  const nDays = numDays(request.departureDate, request.returnDate);
  // Check both the meta doc (set by CF email approval) and the request field (set by in-app approval)
  const skeletalApproved = skeletalMetaApproved || request.skeletalApprovalState === 'approved';
  const hasFullItinerary = itineraryItems.length > 0;
  const unreadCount = 0; // future enhancement

  // Group full itinerary items by day
  // items use 1-indexed `day` field; convert to 0-indexed for dayLabel
  const itemsByDay = {};
  itineraryItems.forEach((item) => {
    const day = (item.day ?? 1) - 1;
    if (!itemsByDay[day]) itemsByDay[day] = [];
    itemsByDay[day].push(item);
  });
  const itinDayKeys = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-[72px]">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-[#0D2240] px-4 pt-safe-top">
        <div className="flex items-center gap-3 py-3">
          <button onClick={() => navigate('/customer')}
            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition flex-shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold text-white truncate">{tourTitle}</p>
            <p className="text-[12px] text-[#93C5FD]">
              {formatDate(request.departureDate)} – {formatDate(request.returnDate)}
            </p>
          </div>
          {request.status && (
            <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-1 rounded-full ${STATUS_STYLES[request.status]?.badge || 'bg-white/20 text-white'}`}>
              {STATUS_LABELS[request.status] || request.status}
            </span>
          )}
        </div>
      </div>

      {/* ── Tab Content ────────────────────────────────────────────────────── */}
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">

        {/* ── Overview ──────────────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Tour Details */}
            <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5">
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-4">Tour Details</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Sport"        value={request.sport} />
                <Field label="Destination"  value={request.destination} />
                <Field label="Departure"    value={formatDate(request.departureDate)} />
                <Field label="Return"       value={formatDate(request.returnDate)} />
                <Field label="Group Type"   value={request.groupType} />
                <Field label="Total travellers" value={totalTravellers(request) ? totalTravellers(request).toString() : null} />
              </div>
              {request.notes && (
                <div className="mt-4 pt-4 border-t border-[#F1F3F6]">
                  <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Additional Requirements</p>
                  <p className="text-[13px] text-[#374151] leading-relaxed">{request.notes}</p>
                </div>
              )}
            </div>

            {/* Quote / Price */}
            {request.quoteDetails && QUOTE_SENT_STATUSES.includes(request.status) && (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5">
                <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-4">Your Quote</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-[#EBF2FC] rounded-[10px] p-4 text-center">
                    <p className="text-[11px] font-semibold text-[#6B7280] mb-1">Total Price</p>
                    <p className="text-[22px] font-extrabold text-pb">
                      {formatAmount(request.quoteDetails.totalCostToCustomer, sym)}
                    </p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-[10px] p-4 text-center">
                    <p className="text-[11px] font-semibold text-[#6B7280] mb-1">Per Person</p>
                    <p className="text-[22px] font-extrabold text-[#374151]">
                      {formatAmount(request.quoteDetails.costPerPersonToCustomer, sym)}
                    </p>
                  </div>
                </div>

                {request.status === 'quote_sent' && !showAmendForm && !request.customerAmendmentRequest && (
                  <div className="flex flex-col gap-2 mt-2">
                    <button onClick={approveQuote} disabled={quoteSubmitting}
                      className="w-full py-3 bg-[#16A34A] text-white font-bold text-[14px] rounded-[10px] transition hover:bg-[#15803D] disabled:opacity-60">
                      ✓ Approve Quote
                    </button>
                    <button onClick={() => setShowAmendForm(true)}
                      className="w-full py-3 bg-[#EBF2FC] text-pb font-bold text-[14px] rounded-[10px] transition hover:bg-pb hover:text-white">
                      Request Amendment
                    </button>
                    <button onClick={() => setConfirmDecline(true)}
                      className="w-full py-3 bg-[#FEF2F2] text-dr font-bold text-[14px] rounded-[10px] transition hover:bg-dr hover:text-white">
                      Decline Quote
                    </button>
                  </div>
                )}

                {showAmendForm && (
                  <div className="mt-3 space-y-2">
                    <textarea value={amendText} onChange={(e) => setAmendText(e.target.value)} rows={3}
                      placeholder="Describe the changes you'd like…"
                      className="w-full border border-[#E5E7EB] rounded-[10px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb resize-none" />
                    <div className="flex gap-2">
                      <button onClick={() => setShowAmendForm(false)}
                        className="flex-1 py-2.5 border border-[#E5E7EB] rounded-[8px] text-[13px] font-semibold text-[#6B7280]">Cancel</button>
                      <button onClick={submitAmendment} disabled={!amendText.trim() || quoteSubmitting}
                        className="flex-1 py-2.5 bg-pb text-white rounded-[8px] text-[13px] font-bold disabled:opacity-60">Send</button>
                    </div>
                  </div>
                )}

                {request.customerAmendmentRequest && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-[10px]">
                    <p className="text-[12px] font-semibold text-amber-800 mb-1">Amendment requested</p>
                    <p className="text-[12px] text-amber-700">{request.customerAmendmentRequest}</p>
                  </div>
                )}
              </div>
            )}

            {/* Rooming notification */}
            {roomingLayout?.status === 'sent' && (
              <div className="bg-[#EBF2FC] border border-[#93C5FD] rounded-[14px] p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-pb flex items-center justify-center flex-shrink-0">
                  {TAB_ICONS.rooming}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-pb">Room layout ready</p>
                  <p className="text-[12px] text-[#2563EB]">MST have set up the rooms — assign participants now.</p>
                </div>
                <button onClick={() => setActiveTab('rooming')}
                  className="px-3 py-1.5 bg-pb text-white text-[12px] font-bold rounded-[8px] flex-shrink-0">
                  Assign
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Participants ───────────────────────────────────────────────────── */}
        {activeTab === 'participants' && (
          <div className="space-y-4">
            {PARTICIPANT_STATUSES.includes(request.status) ? (
              <ParticipantList
                requestId={id}
                expectedCount={totalTravellers(request)}
                editable
              />
            ) : (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
                <p className="text-[14px] font-bold text-[#111827] mb-2">Participant registration not yet open</p>
                <p className="text-[13px] text-[#6B7280]">Participant registration opens once the deposit has been paid.</p>
              </div>
            )}
          </div>
        )}

        {/* ── Invoices ───────────────────────────────────────────────────────── */}
        {activeTab === 'invoices' && (
          <div className="space-y-3">
            {invoices.length === 0 ? (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
                <p className="text-[14px] font-bold text-[#111827] mb-2">No invoices yet</p>
                <p className="text-[13px] text-[#6B7280]">Invoices will appear here once your tour is confirmed.</p>
              </div>
            ) : invoices.map((inv) => (
              <div key={inv.id} className="bg-white rounded-[14px] border border-[#E5E7EB] p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-[14px] font-bold text-[#111827]">{inv.invoiceNumber || 'Invoice'}</p>
                    <p className="text-[12px] text-[#6B7280] mt-0.5">{inv.requestSummary}</p>
                  </div>
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {inv.status === 'paid' ? 'Paid' : 'Unpaid'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wide">Amount</p>
                    <p className="text-[15px] font-bold text-[#111827]">{formatAmount(inv.amount, sym)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wide">Due Date</p>
                    <p className="text-[13px] font-semibold text-[#374151]">{formatDate(inv.dueDate)}</p>
                  </div>
                </div>
                {inv.status !== 'paid' && inv.bankDetails && (
                  <div className="mb-3 p-3 bg-[#F9FAFB] rounded-[10px] text-[12px] text-[#374151] space-y-0.5">
                    <p className="font-semibold text-[#111827] mb-1">Bank Details</p>
                    {inv.bankDetails.split('\n').map((l, i) => <p key={i}>{l}</p>)}
                  </div>
                )}
                <button
                  onClick={async () => { setDownloadingId(inv.id); await downloadInvoicePdf(inv); setDownloadingId(null); }}
                  disabled={downloadingId === inv.id}
                  className="w-full py-2.5 border border-[#E5E7EB] rounded-[8px] text-[13px] font-semibold text-[#374151] hover:bg-[#F9FAFB] transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  {downloadingId === inv.id ? 'Downloading…' : 'Download PDF'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── Itineraries ────────────────────────────────────────────────────── */}
        {activeTab === 'itineraries' && (
          <div className="space-y-4">
            {/* Skeletal itinerary */}
            {skeletalApproved && Object.keys(skeletalLayout.grid).length > 0 ? (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F1F3F6]">
                  <p className="text-[14px] font-bold text-[#111827]">Skeletal Itinerary</p>
                  <p className="text-[12px] text-[#6B7280] mt-0.5">Overview of your daily schedule</p>
                </div>
                <div className="p-5 space-y-5">
                  {Array.from({ length: nDays }, (_, d) => {
                    const daySlots = dayEntries(skeletalLayout, d);
                    if (!daySlots.length) return null;
                    return (
                      <div key={d}>
                        <p className="text-[12px] font-bold text-[#374151] mb-2">{dayLabel(request.departureDate, d)}</p>
                        <div className="space-y-1.5 pl-3 border-l-2 border-[#E5E7EB]">
                          {daySlots.map(({ key, cell }) => (
                            <div key={key} className="flex items-center gap-3">
                              <span className={`text-[10px] font-bold uppercase tracking-wide w-20 flex-shrink-0 ${cell.type === 'flight' ? 'text-[#16A34A]' : cell.type === 'meal' ? 'text-[#D97706]' : 'text-[#9CA3AF]'}`}>
                                {slotLabel(key)}
                              </span>
                              <span className="text-[13px] text-[#111827]">{cell.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
                <p className="text-[14px] font-bold text-[#111827] mb-2">Skeletal itinerary not yet available</p>
                <p className="text-[13px] text-[#6B7280]">Your itinerary overview will appear here once it has been approved.</p>
              </div>
            )}

            {/* Full itinerary */}
            {hasFullItinerary && (
              <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F1F3F6]">
                  <p className="text-[14px] font-bold text-[#111827]">Full Itinerary</p>
                </div>

                {/* Day selector */}
                <div className="border-b border-[#F1F3F6] px-3 py-2 overflow-x-auto scrollbar-hide">
                  <div className="flex gap-1 min-w-max">
                    {itinDayKeys.map((dayIdx) => (
                      <button
                        key={dayIdx}
                        onClick={() => setActiveItinDay(dayIdx)}
                        className={`px-3 py-1.5 rounded-[8px] text-[12px] font-semibold whitespace-nowrap transition ${
                          activeItinDay === dayIdx
                            ? 'bg-navy text-white'
                            : 'text-[#6B7280] hover:bg-[#F3F4F6]'
                        }`}
                      >
                        Day {dayIdx + 1}
                        <span className="block text-[10px] font-normal opacity-75">
                          {dayLabel(request.departureDate, dayIdx).split(' ').slice(0, 2).join(' ')}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Day header + items */}
                {itemsByDay[activeItinDay] && (
                  <div className="p-5">
                    <p className="text-[13px] font-bold text-[#374151] mb-3">
                      {dayLabel(request.departureDate, activeItinDay)}
                    </p>
                    <div className="space-y-2">
                      {itemsByDay[activeItinDay].map((item) => (
                        <div key={item.id} className="flex items-start gap-3 p-3 bg-[#F9FAFB] rounded-[10px]">
                          <span className="text-[12px] font-bold text-[#9CA3AF] w-12 flex-shrink-0 tabular-nums pt-0.5">
                            {item.time || '—'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-[#111827] leading-snug">
                              {item.locationName || item.title || item.activityType}
                            </p>
                            {item.activityType && item.activityType !== (item.locationName || item.title) && (
                              <p className="text-[11px] text-[#9CA3AF] mt-0.5">{item.activityType}</p>
                            )}
                            {item.notes && <p className="text-[12px] text-[#6B7280] mt-0.5 leading-snug">{item.notes}</p>}
                            <ActivityMatches
                              item={item}
                              fixturesById={fixturesById}
                              teamsById={tourTeams.teamsById}
                              departureDate={request.departureDate}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prev / Next navigation */}
                {itinDayKeys.length > 1 && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-[#F1F3F6]">
                    <button
                      onClick={() => setActiveItinDay((d) => itinDayKeys[Math.max(0, itinDayKeys.indexOf(d) - 1)])}
                      disabled={activeItinDay === itinDayKeys[0]}
                      className="flex items-center gap-1 text-[13px] font-semibold text-pb disabled:text-[#D1D5DB] transition"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                      Prev
                    </button>
                    <span className="text-[12px] text-[#9CA3AF]">
                      Day {activeItinDay + 1} of {itinDayKeys.length}
                    </span>
                    <button
                      onClick={() => setActiveItinDay((d) => itinDayKeys[Math.min(itinDayKeys.length - 1, itinDayKeys.indexOf(d) + 1)])}
                      disabled={activeItinDay === itinDayKeys[itinDayKeys.length - 1]}
                      className="flex items-center gap-1 text-[13px] font-semibold text-pb disabled:text-[#D1D5DB] transition"
                    >
                      Next
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Rooming ────────────────────────────────────────────────────────── */}
        {activeTab === 'rooming' && (
          <RoomingView
            roomingLayout={roomingLayout}
            participants={participants}
            roomAssignments={roomAssignments}
            setRoomAssignments={setRoomAssignments}
            draggedParticipant={draggedParticipant}
            setDraggedParticipant={setDraggedParticipant}
            savingRooming={savingRooming}
            onSubmit={submitRooming}
          />
        )}

        {/* ── Teams ──────────────────────────────────────────────────────────── */}
        {activeTab === 'teams' && <TeamsTab requestId={id} mode="coach" />}

        {/* ── Documents ──────────────────────────────────────────────────────── */}
        {activeTab === 'documents' && (
          <div className="space-y-4">
            {/* MST documents */}
            <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#F1F3F6]">
                <p className="text-[13px] font-bold text-[#111827]">Documents from MST</p>
              </div>
              <div className="p-5">
                {documents.length === 0 && sentLetters.filter((l) => l.sharedWithCustomer !== false).length === 0 ? (
                  <p className="text-[13px] text-[#9CA3AF]">No documents shared yet.</p>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <a key={doc.id} href={doc.downloadUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 hover:bg-[#F9FAFB] rounded-[10px] transition group">
                        <FileIcon mime={doc.mimeType} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-[#111827] truncate">{doc.name}</p>
                          {doc.size && <p className="text-[11px] text-[#9CA3AF]">{formatBytes(doc.size)}</p>}
                        </div>
                        <svg className="w-4 h-4 text-[#9CA3AF] group-hover:text-pb transition" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </a>
                    ))}
                    {/* Generated letters have no sharedWithCustomer field and are always
                        visible; staff-uploaded ones are hidden when marked internal-only. */}
                    {sentLetters.filter((l) => l.sharedWithCustomer !== false).map((letter) => letter.downloadUrl && (
                      <a key={letter.id} href={letter.downloadUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 hover:bg-[#F9FAFB] rounded-[10px] transition group">
                        <FileIcon mime="application/pdf" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-[#111827] truncate">{letter.templateName || 'Letter'}</p>
                          <p className="text-[11px] text-[#9CA3AF]">Sent {letter.sentAt?.toDate?.()?.toLocaleDateString('en-GB') || ''}</p>
                        </div>
                        <svg className="w-4 h-4 text-[#9CA3AF] group-hover:text-pb transition" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Risk assessments, certificates and forms sent by the office. One
                row per document, showing the most recent send of each. */}
            {(() => {
              const latest = new Map();
              for (const s of sentResources) {
                for (const item of s.items || []) {
                  if (!item.downloadUrl) continue;
                  const key = `${item.resourceId || item.title}-${item.format}`;
                  const at = s.sentAt?.toDate?.()?.getTime() || 0;
                  const prev = latest.get(key);
                  if (!prev || at > prev.at) latest.set(key, { ...item, at });
                }
              }
              const items = [...latest.values()].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
              if (items.length === 0) return null;
              return (
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
                  <div className="px-5 py-4 border-b border-[#F1F3F6]">
                    <p className="text-[13px] font-bold text-[#111827]">Risk Assessments & Certificates</p>
                    <p className="text-[11.5px] text-[#6B7280] mt-0.5">Everything MST has sent you for this tour — download these any time.</p>
                  </div>
                  <div className="p-5 space-y-2">
                    {items.map((item, i) => (
                      <a key={i} href={item.downloadUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 hover:bg-[#F9FAFB] rounded-[10px] transition group">
                        <FileIcon mime={item.format === 'docx' ? 'application/msword' : 'application/pdf'} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-[#111827] truncate">{item.title}</p>
                          <p className="text-[11px] text-[#9CA3AF]">
                            {item.format === 'docx' ? 'Word' : 'PDF'}
                            {item.size ? ` · ${formatBytes(item.size)}` : ''}
                            {item.at ? ` · sent ${new Date(item.at).toLocaleDateString('en-GB')}` : ''}
                          </p>
                        </div>
                        <svg className="w-4 h-4 text-[#9CA3AF] group-hover:text-pb transition" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Customer uploads */}
            <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#F1F3F6]">
                <p className="text-[13px] font-bold text-[#111827]">Your Documents</p>
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-pb border border-pb/30 rounded-[8px] hover:bg-pb/5 transition disabled:opacity-60">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                  Upload
                </button>
              </div>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleDocumentUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.csv,.txt" />
              <div className="p-5">
                {uploading && (
                  <div className="mb-3">
                    <div className="flex justify-between text-[11px] text-[#6B7280] mb-1">
                      <span>Uploading…</span><span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-[#E5E7EB] rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-pb transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}
                {customerDocs.length === 0 && !uploading ? (
                  <p className="text-[13px] text-[#9CA3AF]">No documents uploaded yet. Use the button above to share files with MST.</p>
                ) : (
                  <div className="space-y-2">
                    {customerDocs.map((doc) => (
                      <a key={doc.id} href={doc.downloadUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 hover:bg-[#F9FAFB] rounded-[10px] transition group">
                        <FileIcon mime={doc.mimeType} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-[#111827] truncate">{doc.name}</p>
                          {doc.size && <p className="text-[11px] text-[#9CA3AF]">{formatBytes(doc.size)}</p>}
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Calendar ───────────────────────────────────────────────────────── */}
        {activeTab === 'calendar' && (
          <TourCalendarView request={request} />
        )}

        {/* ── Messages ───────────────────────────────────────────────────────── */}
        {activeTab === 'messages' && (
          <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
            <MessageThread requestId={id} senderRole="customer" />
          </div>
        )}
      </div>

      {/* ── Fixed Bottom Navigation ─────────────────────────────────────────── */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E7EB] flex overflow-x-auto pb-safe scrollbar-hide">
        {TABS.filter((tab) => tab.key !== 'teams' || hasTeams || activeTab === 'teams').map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-w-[56px] min-h-[56px] transition-colors ${active ? 'text-pb' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}>
              <span className={active ? 'text-pb' : 'text-[#9CA3AF]'}>{TAB_ICONS[tab.key]}</span>
              <span className={`text-[9px] font-semibold tracking-wide whitespace-nowrap ${active ? 'text-pb' : 'text-[#9CA3AF]'}`}>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Confirm decline dialog */}
      {confirmDecline && (
        <ConfirmDialog
          title="Decline this quote?"
          message="This will cancel your tour request. This cannot be undone."
          confirmLabel="Yes, decline"
          onConfirm={declineQuote}
          onCancel={() => setConfirmDecline(false)}
        />
      )}
    </div>
  );
}

// ── Rooming sub-view ──────────────────────────────────────────────────────────

function RoomingView({ roomingLayout, participants, roomAssignments, setRoomAssignments, draggedParticipant, setDraggedParticipant, savingRooming, onSubmit }) {
  if (!roomingLayout || roomingLayout.status === 'draft') {
    return (
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-[#EBF2FC] flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-pb" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>
        </div>
        <p className="text-[14px] font-bold text-[#111827] mb-2">Room layout not yet available</p>
        <p className="text-[13px] text-[#6B7280]">MST will set up your room layout and notify you when it's ready to assign.</p>
      </div>
    );
  }

  const isSubmitted = roomingLayout.status === 'submitted' || roomingLayout.status === 'finalised';
  const rooms = roomingLayout.rooms || [];

  // Build set of already-assigned participant IDs
  const assignedIds = new Set(Object.values(roomAssignments).flat());
  const unassigned = participants.filter((p) => !assignedIds.has(p.id));

  function fullName(p) {
    return [p.firstName, p.surname].filter(Boolean).join(' ') || '—';
  }

  function handleDrop(roomId) {
    if (!draggedParticipant || isSubmitted) return;
    const room = rooms.find((r) => r.id === roomId);
    const currentOccupants = roomAssignments[roomId] || [];
    if (currentOccupants.length >= (room?.beds || 0)) return;
    // Remove from any existing room
    const newAssignments = {};
    Object.entries(roomAssignments).forEach(([rid, pids]) => {
      newAssignments[rid] = pids.filter((pid) => pid !== draggedParticipant.id);
    });
    newAssignments[roomId] = [...(newAssignments[roomId] || []), draggedParticipant.id];
    setRoomAssignments(newAssignments);
    setDraggedParticipant(null);
  }

  function removeFromRoom(roomId, participantId) {
    if (isSubmitted) return;
    setRoomAssignments((prev) => ({
      ...prev,
      [roomId]: (prev[roomId] || []).filter((id) => id !== participantId),
    }));
  }

  return (
    <div className="space-y-4">
      {isSubmitted && (
        <div className="bg-green-50 border border-green-200 rounded-[14px] p-4">
          <p className="text-[13px] font-bold text-green-800">✓ Room assignments submitted</p>
          <p className="text-[12px] text-green-700 mt-0.5">Your room assignments have been saved. MST have been notified.</p>
        </div>
      )}

      {!isSubmitted && unassigned.length > 0 && (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-4">
          <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wide mb-1">
            Unassigned Participants
          </p>
          <p className="text-[11px] text-[#9CA3AF] mb-3">
            Tap to select, then tap a room. On desktop you can also drag.
          </p>
          <div className="flex flex-wrap gap-2">
            {unassigned.map((p) => {
              const isSelected = draggedParticipant?.id === p.id;
              return (
                <div key={p.id}
                  draggable
                  onDragStart={() => setDraggedParticipant(p)}
                  onDragEnd={() => setDraggedParticipant(null)}
                  onClick={() => setDraggedParticipant(isSelected ? null : p)}
                  className={`flex items-center gap-2 px-3 py-1.5 border rounded-full text-[12px] font-semibold cursor-pointer select-none transition-all ${
                    isSelected
                      ? 'bg-pb border-pb text-white scale-105 shadow-md'
                      : 'bg-[#EBF2FC] border-[#93C5FD] text-pb'
                  }`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${isSelected ? 'bg-white text-pb' : 'bg-pb text-white'}`}>
                    {(p.firstName || '?').charAt(0)}
                  </span>
                  {fullName(p)}
                  {isSelected && <span className="text-[10px] opacity-80">✓</span>}
                </div>
              );
            })}
          </div>
          {draggedParticipant && (
            <p className="text-[12px] text-pb font-semibold mt-3 animate-pulse">
              ↓ Tap a room below to place <strong>{fullName(draggedParticipant)}</strong>
            </p>
          )}
        </div>
      )}

      {/* Room cards */}
      {rooms.map((room) => {
        const occupantIds = roomAssignments[room.id] || [];
        const occupants = occupantIds.map((pid) => participants.find((p) => p.id === pid)).filter(Boolean);
        const isFull = occupants.length >= room.beds;
        const isDragTarget = draggedParticipant && !isSubmitted && !isFull;

        return (
          <div key={room.id}
            onDragOver={isDragTarget ? (e) => e.preventDefault() : undefined}
            onDrop={isDragTarget ? () => handleDrop(room.id) : undefined}
            onClick={isDragTarget ? () => handleDrop(room.id) : undefined}
            className={`bg-white rounded-[14px] border-2 transition-all ${
              isDragTarget ? 'border-pb bg-blue-50/30 cursor-pointer shadow-md' :
              isFull       ? 'border-[#E5E7EB] opacity-80' :
                             'border-[#E5E7EB]'
            }`}>
            <div className="px-4 py-3 border-b border-[#F1F3F6] flex items-center justify-between">
              <div>
                <p className="text-[13px] font-bold text-[#111827]">{room.label}</p>
                <p className="text-[11px] text-[#9CA3AF]">{room.beds} bed{room.beds !== 1 ? 's' : ''} · {occupants.length}/{room.beds} assigned</p>
              </div>
              <div className="flex items-center gap-2">
                {isDragTarget && <span className="text-[11px] font-bold text-pb animate-pulse">Tap to place</span>}
                <div className={`w-2.5 h-2.5 rounded-full ${isFull ? 'bg-[#22C55E]' : occupants.length > 0 ? 'bg-[#F59E0B]' : 'bg-[#E5E7EB]'}`} />
              </div>
            </div>
            <div className="p-4 min-h-[60px]">
              {occupants.length === 0 ? (
                <p className={`text-[12px] text-center py-2 ${isDragTarget ? 'text-pb font-semibold' : 'text-[#D1D5DB]'}`}>
                  {isDragTarget ? '+ Place here' : 'Empty'}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {occupants.map((p) => (
                    <div key={p.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full text-[12px] text-[#374151]">
                      <span className="w-4 h-4 rounded-full bg-[#6B7280] text-white flex items-center justify-center text-[8px] font-bold flex-shrink-0">
                        {(p.firstName || '?').charAt(0)}
                      </span>
                      {fullName(p)}
                      {!isSubmitted && (
                        <button onClick={(e) => { e.stopPropagation(); removeFromRoom(room.id, p.id); }} className="text-[#9CA3AF] hover:text-dr ml-0.5 leading-none">×</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {!isSubmitted && (
        <button onClick={onSubmit} disabled={savingRooming || unassigned.length > 0}
          className="w-full py-3.5 bg-pb text-white font-bold text-[14px] rounded-[12px] transition hover:bg-navy disabled:opacity-50">
          {savingRooming ? 'Saving…' : unassigned.length > 0 ? `${unassigned.length} participant${unassigned.length !== 1 ? 's' : ''} still unassigned` : 'Submit Room Assignments'}
        </button>
      )}
    </div>
  );
}

// ── Tour calendar sub-view ────────────────────────────────────────────────────

function TourCalendarView({ request }) {
  const dep = request.departureDate ? new Date(request.departureDate) : null;
  const ret = request.returnDate   ? new Date(request.returnDate)   : null;

  if (!dep) {
    return (
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
        <p className="text-[13px] text-[#9CA3AF]">No dates set yet.</p>
      </div>
    );
  }

  // Build a month view centred on departure month
  const year  = dep.getFullYear();
  const month = dep.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7; // Mon-first
  const days = [];
  for (let i = 0; i < startPad; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));

  const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const monthLabel = firstDay.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  function inRange(d) {
    if (!d || !dep || !ret) return false;
    return d >= dep && d <= ret;
  }
  function isStart(d) { return d && dep && d.toDateString() === dep.toDateString(); }
  function isEnd(d)   { return d && ret && d.toDateString() === ret.toDateString(); }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5">
        <p className="text-[14px] font-bold text-[#111827] mb-4 text-center">{monthLabel}</p>
        <div className="grid grid-cols-7 mb-2">
          {DAYS_OF_WEEK.map((d) => (
            <p key={d} className="text-[11px] font-semibold text-[#9CA3AF] text-center">{d}</p>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {days.map((d, i) => {
            const start  = isStart(d);
            const end    = isEnd(d);
            const range  = inRange(d);
            return (
              <div key={i} className={`h-9 flex items-center justify-center text-[13px] font-medium rounded-full ${
                start || end  ? 'bg-pb text-white font-bold' :
                range         ? 'bg-[#EBF2FC] text-pb' :
                d             ? 'text-[#374151]' : ''
              }`}>
                {d?.getDate() || ''}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-pb flex-shrink-0" />
          <div>
            <p className="text-[11px] text-[#9CA3AF] font-semibold uppercase tracking-wide">Departure</p>
            <p className="text-[14px] font-bold text-[#111827]">{dep.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
        {ret && (
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-pb flex-shrink-0" />
            <div>
              <p className="text-[11px] text-[#9CA3AF] font-semibold uppercase tracking-wide">Return</p>
              <p className="text-[14px] font-bold text-[#111827]">{ret.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
        )}
        {dep && ret && (
          <p className="text-[13px] text-[#6B7280] pl-6">
            {Math.round((ret - dep) / 86400000)} nights
          </p>
        )}
      </div>
    </div>
  );
}
